import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../redux/store";
import { setFiltersApplied } from "../../../redux/features/whoIsIn";
import { DepartmentMaster, GetWhoIsInRequest, SiteType } from "../../../types";
import { EmployeeViewType } from "../../../enums/employeeViewType";
import { DateTimeUtil } from "../../../utils";
import MobileFilterComponent from "../../../components/mobile/mobileDashboard/filterComponent";
import { Filter } from "lucide-react";
import { getDepartments, getSites } from "../../../redux/features/common";
import { getWhoIsWorking } from "../../../redux/features/whoIsIn";
import { NgwAvatar } from "../../../components/common";
import { useNavigate } from "react-router";

const ViewEmployees: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { employees, isLoading, meta, requestStatus } = useSelector((state: RootState) => state.whoIsIn);
    const { sites, departments } = useSelector((state: RootState) => state.common);
    const filterRef = useRef<HTMLDivElement>(null);
    const [page, setPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [selectedSites, setSelectedSites] = useState<SiteType[]>([]);
    const [selectedDepartments, setSelectedDepartments] = useState<DepartmentMaster[]>([]);

    const fetchFilterOptions = async () => {
        await Promise.all([dispatch(getSites()), dispatch(getDepartments())]);
    };

    useEffect(() => {
        // Close filter on outside click
        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
                setIsFilterOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);

        fetchFilterOptions();

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if ((meta?.page || 0) < Math.ceil((meta?.count || 0) / (meta?.limit || 0))) {
            dispatch(
                getWhoIsWorking({
                    DEPARTMENT_CODE: selectedDepartments.map((dept) => dept.department_code!.toString()),
                    USERSITES: selectedSites.map((site) => site.us_user_site_profile_id!.toString()),
                    current_datetime: DateTimeUtil.formatDateToMySQL(new Date()),
                })
            );
        }
    }, [dispatch, page, meta?.limit]);

    const handleScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop === clientHeight && !isLoading) {
            setPage((prevPage) => prevPage + 1);
        }
    };

    const applyFilters = (selectedSiteObjects: SiteType[], selectedDepartmentObjects: DepartmentMaster[]) => {
        setSelectedSites(selectedSiteObjects);
        setSelectedDepartments(selectedDepartmentObjects);
        dispatch(setFiltersApplied(true));
        setIsFilterOpen(false);
    };

    useEffect(() => {
        dispatch(
            getWhoIsWorking({
                DEPARTMENT_CODE: selectedDepartments.map((dept) => dept.department_code!.toString()),
                USERSITES: selectedSites.map((site) => site.us_user_site_profile_id!.toString()),
                current_datetime: DateTimeUtil.formatDateToMySQL(new Date()),
            })
        );
    }, [selectedSites, selectedDepartments]);

    const filteredEmployees = employees?.filter((employee) => {
        const matchesSearch = searchQuery
            ? employee.employee_name?.toLowerCase().includes(searchQuery.toLowerCase())
            : true;
        const matchesSite =
            selectedSites.length === 0 ||
            selectedSites.some((site) => site.us_user_site_profile_id === employee.default_site_id);
        return matchesSearch && matchesSite;
    });

    return (
        <div className="bg-white p-4 rounded-lg shadow-md">
            {/* <h2 className="text-lg text-center text-blue-500 font-bold mb-4">{title[viewType]}</h2> */}

            {/* Search and Filter */}
            <div className="flex items-center gap-2 mb-4">
                <input
                    type="text"
                    placeholder="Search by employee name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button className="text-gray-600 hover:text-gray-900" onClick={() => setIsFilterOpen(!isFilterOpen)}>
                    <Filter />
                </button>
            </div>

            {/* Filter Dropdown */}
            {isFilterOpen && (
                <div ref={filterRef}>
                    <MobileFilterComponent
                        sites={sites}
                        departments={departments}
                        applyFilters={applyFilters}
                        clearFilters={() => {
                            setSelectedSites([]);
                            setSelectedDepartments([]);
                            dispatch(setFiltersApplied(false));
                        }}
                        onClose={() => setIsFilterOpen(false)}
                    />
                </div>
            )}

            {/* Employee List */}
            <div onScroll={handleScroll} className="h-[80vh] overflow-y-auto space-y-4">
                {filteredEmployees && filteredEmployees.length > 0 ? (
                    filteredEmployees.map((employee) => (
                        <div
                            key={employee.os_emp_account_id}
                            className="flex items-center p-4 border border-gray-200 rounded-lg gap-4"
                            onClick={() => {
                                if (employee?.raw_id) {
                                    navigate("/m-who-is-in-details", {
                                        state: {
                                            raw_data_id: employee.raw_id,
                                        },
                                    });
                                }
                            }}
                        >
                            <NgwAvatar
                                src={employee.profile_image}
                                name={employee.employee_name || "Unknown"}
                                className="w-16 h-16 rounded-full"
                            />
                            <div>
                                <p className="font-normal">{employee.employee_name || "Unknown"}</p>
                                <p className="text-gray-500 text-sm">
                                    <span className="font-semibold">In:</span>{" "}
                                    {DateTimeUtil.FormatWithDayjsUtc(
                                        employee.start_datetime,
                                        DateTimeUtil.DATETIME_FANCY_2
                                    ) || "N/A"}{" "}
                                    {/* ({DateTimeUtil.FormatDurationFromStart(employee.start_datetime)}) */}
                                </p>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="text-center mt-4 text-gray-500">No employees found.</div>
                )}

                {isLoading && (
                    <div className="flex justify-center mt-4">
                        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ViewEmployees;
